import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import useFieldSetErrors from 'hooks/useFieldSetErrors';
import { useToast } from 'hooks/useToast';
import TypeResource from 'enum/TypeResource';
import TypeAction from 'enum/TypeAction';
import useUser from 'hooks/useUser';
import { useApiDelete, useApiGet, useApiPost, useApiPut } from 'hooks/useApi';
import ToastType from 'enum/ToastType';
import apiPaths from 'constants/apiPaths';
import { ExternalNews } from 'models/ExternalNews';
import Pagination from 'models/Pagination';
import NavCorporateTv from 'pages/CorporateTV/_presenters/enum/navCorporateTv';

const schema = yup
  .object({
    name: yup.string().required('O título é obrigatório.'),
    category: yup.object().required('A categoria é obrigatória.'),
    quantityItems: yup
      .number()
      .default(1)
      .max(5, 'A quantidade máxima de itens é 5.')
      .required('A quantidade de itens é obrigatória.'),
    isActive: yup.boolean().default(true)
  })
  .required();

const useControllerExternalNewsFormPage = () => {
  const params = useParams();
  const user = useUser();
  const navigate = useNavigate();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const { data, isLoading: isFetching } = useApiGet<ExternalNews>(
    apiPaths.externalNews,
    { id: params.id, enabled: !!params.id }
  );

  const { data: externalNewsCategories } = useApiGet<
    Pagination<ExternalNews['category']>
  >(apiPaths.externalNewsCategories);

  const { mutateAsync: mutateCreate, isPending: isCreating } = useApiPost<
    ExternalNews
  >(apiPaths.externalNews);

  const { mutateAsync: mutateUpdate, isPending: isUpdating } = useApiPut<
    ExternalNews
  >(apiPaths.externalNews, params.id);

  const { mutate: mutateDelete, isPending: isDeleting } = useApiDelete(
    apiPaths.externalNews,
    params.id
  );

  const mutate = params.id ? mutateUpdate : mutateCreate;

  const isSaving = isCreating || isUpdating;

  const externalNews = params.id ? data : undefined;

  const userCanPerformAction = useMemo(() => {
    if (params.id) {
      return user?.resources?.[TypeResource.NEWS]?.includes(TypeAction.UPDATE);
    } else {
      return user?.resources?.[TypeResource.NEWS]?.includes(TypeAction.CREATE);
    }
  }, [user, params.id]);

  const userCanDelete = useMemo(() => {
    return user?.resources?.[TypeResource.NEWS]?.includes(TypeAction.DELETE);
  }, [user]);

  const formMethods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      isActive: true
    }
  });

  const onError = useFieldSetErrors(formMethods.setError);
  const { addToast } = useToast();

  const onSubmitHandler = useCallback(
    async (data: any) => {
      try {
        await mutate({
          ...data,
          category: data.category.value
        });

        addToast(ToastType.SUCCESS, 'Notícia salva com sucesso');
        navigate(
          `/media-manager/corporate-tv?tab=${NavCorporateTv.EXTERNAL_NEWS}`
        );
      } catch (error) {
        console.error('Erro ao chamar mutate:', error);
        onError(error);
        addToast(ToastType.ERROR, 'Erro ao salvar notícia');
      }
    },
    [mutate, addToast, navigate, onError]
  );

  const handleOnDelete = useCallback(() => {
    if (!params.id || !userCanDelete) return;
    mutateDelete(null as any, {
      onError: (response: any) => {
        addToast(
          ToastType.ERROR,
          response.errors?.detail?.toString() || 'Erro ao excluir notícia'
        );
      },
      onSuccess: () => {
        addToast(ToastType.SUCCESS, 'Notícia excluída com sucesso');
        navigate(
          `/media-manager/corporate-tv?tab=${NavCorporateTv.EXTERNAL_NEWS}`
        );
      }
    });
  }, [addToast, mutateDelete, navigate, params.id, userCanDelete]);

  const goOut = () => {
    navigate(`/media-manager/corporate-tv?tab=${NavCorporateTv.EXTERNAL_NEWS}`);
  };

  const typeOptionsActive = [
    { value: true, label: 'Ativo' },
    {
      value: false,
      label: 'Inativo'
    }
  ];

  const categoriesOptions = useMemo(() => {
    return (
      externalNewsCategories?.items.map((category) => ({
        value: category.id,
        label: category.name
      })) || []
    );
  }, [externalNewsCategories]);

  useEffect(() => {
    if (externalNews) {
      formMethods.reset({
        name: externalNews.name,
        isActive: externalNews.isActive,
        category: {
          value: externalNews.category?.id,
          label: externalNews.category?.name
        },
        quantityItems: externalNews.quantityItems
      } as any);
    }
  }, [externalNews, formMethods]);

  return {
    formMethods,
    onSubmitHandler,
    handleOnDelete,
    isSaving,
    userCanPerformAction,
    userCanDelete,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    isFetching,
    externalNews,
    params,
    isDeleting,
    goOut,
    typeOptionsActive,
    categoriesOptions
  };
};

export default useControllerExternalNewsFormPage;
